const APP_ID = process.env.REACT_APP_ONESIGNAL_APP_ID || '6ea91c49-babd-4c57-8c54-827063101402';

let initializationPromise = null;
const GLOBAL_INIT_KEY = '__allDeliveryOneSignalInitialization';
const GLOBAL_READY_KEY = '__allDeliveryOneSignalReady';

function getEnvironment() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return { isIos: false, isStandalone: false, requiresInstallation: false };
  }

  // iPadOS pode se identificar como Mac, por isso o teste de pontos de toque.
  const userAgent = navigator.userAgent || '';
  const isIos = /iPad|iPhone|iPod/i.test(userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isStandalone = Boolean(
    window.matchMedia?.('(display-mode: standalone)').matches
    || window.navigator.standalone === true
  );

  return {
    isIos,
    isStandalone,
    requiresInstallation: isIos && !isStandalone
  };
}

function isSupported() {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const secureOrigin = typeof window !== 'undefined' && (
    window.isSecureContext ||
    window.location.protocol === 'https:' ||
    hostname === 'localhost' ||
    hostname === '127.0.0.1'
  );
  return Boolean(
    APP_ID &&
    typeof window !== 'undefined' &&
    secureOrigin &&
    'Notification' in window &&
    'serviceWorker' in navigator
  );
}

function withOneSignal(callback) {
  window.OneSignalDeferred = window.OneSignalDeferred || [];
  return new Promise((resolve, reject) => {
    window.OneSignalDeferred.push(async (OneSignal) => {
      try {
        resolve(await callback(OneSignal));
      } catch (error) {
        reject(error);
      }
    });
  });
}

function initialize() {
  if (!isSupported()) return Promise.resolve(null);
  if (window[GLOBAL_INIT_KEY]) return window[GLOBAL_INIT_KEY];
  if (!initializationPromise) {
    initializationPromise = withOneSignal(async (OneSignal) => {
      if (!window[GLOBAL_READY_KEY]) {
        try {
          await OneSignal.init({
            appId: APP_ID,
            serviceWorkerPath: '/push/onesignal/OneSignalSDKWorker.js',
            serviceWorkerParam: { scope: '/push/onesignal/' },
            allowLocalhostAsSecureOrigin: process.env.NODE_ENV !== 'production',
            notifyButton: { enable: false }
          });
        } catch (error) {
          // React StrictMode, navegacao do PWA ou um bundle antigo ainda em
          // cache podem tentar iniciar o mesmo SDK novamente. Isso nao e falha.
          if (!/already initialized/i.test(String(error?.message || error))) throw error;
        }
        window[GLOBAL_READY_KEY] = true;
      }
      return OneSignal;
    }).catch((error) => {
      initializationPromise = null;
      delete window[GLOBAL_INIT_KEY];
      throw error;
    });
    window[GLOBAL_INIT_KEY] = initializationPromise;
  }
  return initializationPromise;
}

async function waitForSubscriptionId(OneSignal) {
  for (let attempt = 0; attempt < 10; attempt += 1) {
    const id = OneSignal?.User?.PushSubscription?.id;
    if (id) return id;
    await new Promise((resolve) => setTimeout(resolve, 200));
  }
  return OneSignal?.User?.PushSubscription?.id || null;
}

async function persistSubscription(subscriptionId) {
  if (!subscriptionId) return;
  const token = localStorage.getItem('@DeliveryAppWeb:token')
    || localStorage.getItem('@DeliveryAdmin:token');
  if (!token) return;

  const apiUrl = process.env.REACT_APP_API_URL || 'https://all-delivery.onrender.com/api';
  const response = await fetch(`${apiUrl.replace(/\/$/, '')}/users/push-token`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ push_token: subscriptionId })
  });
  if (!response.ok) throw new Error('Nao foi possivel vincular este navegador ao usuario');
}

async function identify(user) {
  if (!user?.id) return null;
  const OneSignal = await initialize();
  if (!OneSignal) return null;
  await OneSignal.login(String(user.id));
  await OneSignal.User.addTags({
    role: String(user.user_type || ''),
    lanchonete_id: String(user.lanchonete_id || '')
  });
  return OneSignal;
}

async function requestPermission(user) {
  const environment = getEnvironment();
  if (environment.requiresInstallation) {
    const error = new Error('No iPhone, adicione o All Delivery a Tela de Inicio e abra pelo icone para ativar notificacoes.');
    error.code = 'IOS_INSTALL_REQUIRED';
    throw error;
  }

  const OneSignal = await initialize();
  if (!OneSignal) throw new Error('OneSignal nao configurado neste ambiente');

  // No iOS, a permissao precisa ser solicitada imediatamente apos o toque do
  // usuario. Login, tags e chamadas de rede ficam para depois do prompt.
  await OneSignal.Notifications.requestPermission();
  if (!OneSignal.Notifications.permission) {
    const error = new Error('A permissao foi bloqueada. Ative em Ajustes > Notificacoes > All Delivery.');
    error.code = 'NOTIFICATION_PERMISSION_DENIED';
    throw error;
  }

  if (OneSignal.Notifications.permission && !OneSignal.User.PushSubscription.optedIn) {
    await OneSignal.User.PushSubscription.optIn();
  }
  if (user?.id) {
    await OneSignal.login(String(user.id));
    await OneSignal.User.addTags({
      role: String(user.user_type || ''),
      lanchonete_id: String(user.lanchonete_id || '')
    });
  }
  const subscriptionId = await waitForSubscriptionId(OneSignal);
  if (!subscriptionId) {
    throw new Error('O iPhone permitiu as notificacoes, mas nao concluiu a inscricao. Feche e abra o app pela Tela de Inicio e tente novamente.');
  }
  await persistSubscription(subscriptionId).catch((error) => {
    // O alias external_id continua valido; o token salvo e uma redundancia.
    console.warn('[OneSignal Web] token alternativo:', error.message);
  });
  return {
    permission: Boolean(OneSignal.Notifications.permission),
    subscriptionId,
    optedIn: Boolean(OneSignal.User.PushSubscription.optedIn)
  };
}

async function getStatus(user) {
  if (!isSupported()) return { supported: false, permission: false, optedIn: false };
  const OneSignal = await identify(user);
  const subscriptionId = OneSignal.User.PushSubscription.id || null;
  if (OneSignal.Notifications.permission && OneSignal.User.PushSubscription.optedIn && subscriptionId) {
    persistSubscription(subscriptionId).catch((error) => {
      console.warn('[OneSignal Web] token alternativo:', error.message);
    });
  }
  return {
    supported: true,
    permission: Boolean(OneSignal.Notifications.permission),
    optedIn: Boolean(OneSignal.User.PushSubscription.optedIn),
    subscriptionId
  };
}

async function logout() {
  if (!isSupported()) return;
  const OneSignal = await initialize();
  if (OneSignal) await OneSignal.logout();
}

const OneSignalWebService = { isSupported, getEnvironment, identify, requestPermission, getStatus, logout };

export default OneSignalWebService;
